/**
 * T-044: upload a legacy QR sticker for a unit.
 *
 * Calls T-042 POST /admin/units/:unitId/qr-codes (mode=upload). Surfaces:
 *   - 409 payload_collision (with optional same-tenant conflict info)
 *   - 409 payload_blocklisted
 *   - 422 qr_decode_failed
 *   - 422 payload_mismatch (with decoded preview)
 *
 * Arbitrary-payload override is intentionally NOT exposed in this
 * dialog (deferred). Footnote points users at the on-call superadmin.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { uploadUnitQr, QrApiError } from '@/lib/qrCodeApi';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED_MIME = ['image/png', 'image/jpeg'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  unitCode: string;
  onUploaded: () => void;
}

export function QrUploadDialog({
  open,
  onOpenChange,
  unitId,
  unitCode,
  onUploaded,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setFile(null);
    setLabel('');
    setSubmitting(false);
  }

  async function handleSubmit() {
    if (!file) return;
    if (!ACCEPTED_MIME.includes(file.type)) {
      toast.error('Only PNG or JPEG images are accepted.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image must be 2MB or smaller.');
      return;
    }

    setSubmitting(true);
    try {
      await uploadUnitQr(unitId, file, { label: label.trim() || undefined });
      toast.success('QR uploaded and activated.');
      reset();
      onUploaded();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof QrApiError) {
        const code = err.body?.code;
        if (code === 'payload_collision') {
          if (err.body?.conflict?.unit_id) {
            toast.error(
              `This QR is already attached to another unit (${err.body.conflict.unit_id.slice(0, 8)}…).`,
            );
          } else {
            toast.error('This QR is already attached to a different unit.');
          }
        } else if (code === 'payload_blocklisted') {
          toast.error(
            'This payload was previously hard-deleted. Ask a superadmin to unblock it.',
          );
        } else if (code === 'qr_decode_failed') {
          toast.error("Couldn't read a QR from that image. Re-photograph and try again.");
        } else if (code === 'payload_mismatch') {
          const preview = err.body?.preview;
          toast.error(
            `That QR encodes "${preview ?? '<unknown>'}", which doesn't match this unit (${unitCode}).`,
          );
        } else {
          toast.error(err.body?.message || 'Upload failed.');
        }
      } else {
        toast.error('Upload failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload existing QR</DialogTitle>
          <DialogDescription>
            Attach a legacy QR sticker to <span className="font-mono">{unitCode}</span>. The image
            must encode a payload that matches this unit
            (bare unit code or <span className="font-mono">/u/{unitCode}</span>).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="qr-image">Image (PNG or JPEG, ≤2MB)</Label>
            <Input
              id="qr-image"
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="qr-label">Label (optional)</Label>
            <Input
              id="qr-label"
              type="text"
              maxLength={200}
              placeholder="e.g. Pre-2024 sticker run"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Need to attach a non-matching QR? Ask the on-call superadmin — that path
            requires explicit override and is logged separately.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!file || submitting}>
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
